/**
 * The browse bar's filtering: multi-select pills, a builder for a named
 * expression over any field, and the saved list that makes one a single click.
 *
 * Why it lives beside the pills rather than replacing them: the three pills
 * answer the question people actually ask most of the time ("approved policies
 * I look after"), and a builder that has to be opened for that is a worse bar.
 * So the pills stay, they simply became multi-select, and everything the
 * builder adds is ANDed with them. Nothing here REPLACES a selection somebody
 * has already made — which is what lets a saved filter and the live pills be
 * on at the same time.
 *
 * The condition vocabulary is the transaction engine's (`{field, op, value}`,
 * groups joined by and/or), compiled to SQL server-side by
 * `store.browse_conditions_sql`. This file never evaluates a filter itself:
 * the list is paged, so filtering the loaded page would report a total that is
 * a lie.
 */

import {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import {
  humaniseState, STATE_ORDER,
  type DmsCondition, type DmsConditionGroup, type DmsDocType, type DmsField,
  type DmsFilterSpec, type DmsFolder, type DmsSavedFilter, type DmsUser,
} from './contracts'
import { lookupOptions } from './MetadataFieldInput'
import {
  btnPrimaryStyle, btnStyle, inputStyle, RADIUS, T, TYPE,
} from './styles'
import { Icon } from './icons'

// ── the field catalogue ────────────────────────────────────────────────────

export type FilterFieldKind = 'text' | 'key' | 'date' | 'number' | 'boolean'

export interface FilterOption { value: string; label: string }

export interface FilterField {
  /** `doc:<property>` or `meta:<field_key>` — the names the server compiles. */
  value: string
  label: string
  /** Section heading in the picker. */
  group: string
  kind: FilterFieldKind
  /** Present when the field has a fixed set of legal values. */
  options?: FilterOption[]
}

const DOC_FIELD_ORDER: Array<[string, string, FilterFieldKind]> = [
  ['doc:title', 'Name', 'text'],
  ['doc:doctype', 'Kind of document', 'key'],
  ['doc:state', 'Status', 'key'],
  ['doc:owner', 'Who looks after it', 'key'],
  ['doc:folder', 'Folder', 'key'],
  ['doc:created', 'Filed', 'date'],
  ['doc:updated', 'Last changed', 'date'],
]

/** What a metadata field's declared type means for the operators offered. */
function kindOfField(field: DmsField): FilterFieldKind {
  const type = String(field.data_type || 'VARCHAR').toUpperCase()
  // A DATETIME filters like a date: the KIND here decides which operators a
  // filter offers (before / after / between), and "before 3pm on the 4th" is
  // not one of them. The time of day is captured and shown; it is not a
  // browse operator.
  if (type === 'DATE' || type === 'DATETIME') return 'date'
  if (type === 'NUMERIC') return 'number'
  if (type === 'BOOLEAN') return 'boolean'
  if (type === 'USER') return 'key'
  return 'text'
}

/**
 * Every field a condition may name: the document's own properties, then the
 * metadata fields of every type in the repository.
 *
 * Metadata fields are deduplicated by KEY, because a filter is written against
 * the stored key and several types can declare the same one — the label says
 * which types it came from, so "Effective date" on three kinds of document
 * reads as one field rather than three identical entries.
 */
export function buildFilterFields(
  types: DmsDocType[], folders: DmsFolder[], users?: DmsUser[],
): FilterField[] {
  const out: FilterField[] = []
  const optionsFor = (value: string): FilterOption[] | undefined => {
    if (value === 'doc:doctype') {
      return types.map((t) => ({ value: t.id, label: t.title || t.type_key }))
    }
    if (value === 'doc:state') {
      return STATE_ORDER.map((key) => ({ value: key, label: humaniseState(key) }))
    }
    if (value === 'doc:folder') {
      return folders.map((f) => ({ value: f.id, label: f.name }))
    }
    if (value === 'doc:owner' && users) {
      return users.map((u) => ({ value: u.user_key, label: u.name || u.user_key }))
    }
    return undefined
  }
  for (const [value, label, kind] of DOC_FIELD_ORDER) {
    out.push({ value, label, kind, group: 'The document', options: optionsFor(value) })
  }

  const seen = new Map<string, { field: FilterField; types: string[] }>()
  for (const type of types) {
    for (const field of type.fields || []) {
      const key = String(field.field_key || '').trim()
      if (!key) continue
      const existing = seen.get(key)
      if (existing) {
        existing.types.push(type.title || type.type_key)
        continue
      }
      const choices = lookupOptions(field)
      seen.set(key, {
        types: [type.title || type.type_key],
        field: {
          value: `meta:${key}`,
          label: field.title || key,
          kind: kindOfField(field),
          group: 'Details on the document',
          // `lookupOptions` returns the legal values themselves — a declared
          // list is its own label, exactly as the metadata form shows it.
          options: choices.length
            ? choices.map((c) => ({ value: String(c), label: String(c) }))
            : undefined,
        },
      })
    }
  }
  for (const { field, types: owners } of seen.values()) {
    // Only worth saying when it is NOT universal — on a repository with one
    // kind of document the suffix would be on every row and inform nobody.
    out.push(owners.length > 1 && owners.length < types.length
      ? { ...field, label: `${field.label} (${owners.length} kinds)` }
      : field)
  }
  return out
}

// ── operators ──────────────────────────────────────────────────────────────

export interface OperatorChoice { value: string; label: string; operands: 0 | 1 | 2 | 'many' }

const TEXT_OPS: OperatorChoice[] = [
  { value: 'contains', label: 'contains', operands: 1 },
  { value: 'not_contains', label: 'does not contain', operands: 1 },
  { value: 'eq', label: 'is', operands: 1 },
  { value: 'ne', label: 'is not', operands: 1 },
  { value: 'starts', label: 'starts with', operands: 1 },
  { value: 'ends', label: 'ends with', operands: 1 },
  { value: 'empty', label: 'is empty', operands: 0 },
  { value: 'not_empty', label: 'has any value', operands: 0 },
]

const KEY_OPS: OperatorChoice[] = [
  { value: 'in', label: 'is any of', operands: 'many' },
  { value: 'not_in', label: 'is none of', operands: 'many' },
  { value: 'empty', label: 'is empty', operands: 0 },
  { value: 'not_empty', label: 'has any value', operands: 0 },
]

const ORDERED_OPS: OperatorChoice[] = [
  { value: 'between', label: 'is between', operands: 2 },
  { value: 'ge', label: 'is on or after', operands: 1 },
  { value: 'le', label: 'is on or before', operands: 1 },
  { value: 'eq', label: 'is', operands: 1 },
  { value: 'empty', label: 'is empty', operands: 0 },
  { value: 'not_empty', label: 'has any value', operands: 0 },
]

const NUMBER_OPS: OperatorChoice[] = [
  { value: 'eq', label: 'is', operands: 1 },
  { value: 'ne', label: 'is not', operands: 1 },
  { value: 'gt', label: 'is more than', operands: 1 },
  { value: 'lt', label: 'is less than', operands: 1 },
  { value: 'between', label: 'is between', operands: 2 },
  { value: 'empty', label: 'is empty', operands: 0 },
  { value: 'not_empty', label: 'has any value', operands: 0 },
]

const BOOLEAN_OPS: OperatorChoice[] = [
  { value: 'eq', label: 'is', operands: 1 },
  { value: 'empty', label: 'is empty', operands: 0 },
]

export function operatorsFor(field?: FilterField): OperatorChoice[] {
  if (!field) return TEXT_OPS
  if (field.options?.length) return KEY_OPS
  if (field.kind === 'date') return ORDERED_OPS
  if (field.kind === 'number') return NUMBER_OPS
  if (field.kind === 'boolean') return BOOLEAN_OPS
  return TEXT_OPS
}

// ── spec ↔ conditions ──────────────────────────────────────────────────────

/**
 * A saved filter's whole state as ONE condition group.
 *
 * This is what lets several filters be active together: each becomes a group,
 * and the groups AND. Expressing the pills as conditions rather than pushing
 * them back into the live pills is deliberate — applying a saved filter must
 * never silently rewrite the selection somebody can see in the bar.
 */
export function specToConditions(spec?: DmsFilterSpec | null): DmsConditionGroup | null {
  if (!spec) return null
  const conditions: Array<DmsCondition | DmsConditionGroup> = []
  if (spec.query?.trim()) {
    conditions.push({ field: 'doc:title', op: 'contains', value: spec.query.trim() })
  }
  if (spec.doctypeIds?.length) {
    conditions.push({ field: 'doc:doctype', op: 'in', value: spec.doctypeIds })
  }
  if (spec.states?.length) {
    conditions.push({ field: 'doc:state', op: 'in', value: spec.states })
  }
  if (spec.owners?.length) {
    conditions.push({ field: 'doc:owner', op: 'in', value: spec.owners })
  }
  if (spec.conditions && spec.conditions.conditions?.length) {
    conditions.push(spec.conditions)
  }
  return conditions.length ? { join: 'and', conditions } : null
}

/** How many comparisons a group holds, nested ones included. */
export function countConditions(group?: DmsConditionGroup | null): number {
  if (!group) return 0
  let total = 0
  for (const child of group.conditions || []) {
    const nested = child as DmsConditionGroup
    total += Array.isArray(nested.conditions) ? countConditions(nested) : 1
  }
  return total
}

/** Drop empty groups and conditions with no operand, so an abandoned half-typed
 *  row never becomes a filter that hides documents. */
export function pruneGroup(group: DmsConditionGroup): DmsConditionGroup | null {
  const kept: Array<DmsCondition | DmsConditionGroup> = []
  for (const child of group.conditions || []) {
    const nested = child as DmsConditionGroup
    if (Array.isArray(nested.conditions)) {
      const cleaned = pruneGroup(nested)
      if (cleaned) kept.push(cleaned)
      continue
    }
    const cond = child as DmsCondition
    if (!cond.field) continue
    const operands = operandCount(cond.op)
    if (operands === 0) { kept.push(cond); continue }
    if (operands === 2) {
      if (hasValue(cond.from) && hasValue(cond.to)) kept.push(cond)
      continue
    }
    if (Array.isArray(cond.value)) {
      if (cond.value.length) kept.push(cond)
      continue
    }
    if (hasValue(cond.value)) kept.push(cond)
  }
  return kept.length ? { join: group.join || 'and', conditions: kept } : null
}

function hasValue(value: unknown): boolean {
  return value !== undefined && value !== null && String(value).trim() !== ''
}

function operandCount(op: string): 0 | 1 | 2 | 'many' {
  const all = [...TEXT_OPS, ...KEY_OPS, ...ORDERED_OPS, ...NUMBER_OPS]
  return all.find((o) => o.value === op)?.operands ?? 1
}

// ── a popover that closes the way people expect ────────────────────────────

/**
 * A dropdown anchored to its trigger and rendered in a PORTAL.
 *
 * The portal is not decoration. These pills sit inside two ancestors that
 * clip: the browse bar lives in an `overflow: hidden` box (that is what lets
 * it collapse on scroll), and the builder's condition list scrolls. An
 * absolutely-positioned panel inside either is CLIPPED — it opens, paints
 * nowhere, and reads as a control that does not work. Rendering to
 * `document.body` at the trigger's measured position is the only placement no
 * ancestor can crop.
 */
function usePopover() {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [at, setAt] = useState<Placement | null>(null)

  /**
   * Where the panel goes, measured from the trigger and clamped to the
   * viewport on every side.
   *
   * The clamping is the point. A pill near the bottom of the pane — which the
   * browse bar often is — has less room below it than the panel is tall, and a
   * panel with a fixed height and no clamp is simply placed off the bottom of
   * the window: it opens, and nothing is on screen to see. So the side is
   * chosen by which has more room, the height is cut to the room that exists
   * (the panel scrolls inside), and `left` is pulled back when the panel would
   * run off the right edge.
   */
  const place = useCallback(() => {
    const el = triggerRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const roomBelow = window.innerHeight - rect.bottom - GAP - EDGE
    const roomAbove = rect.top - GAP - EDGE
    // Below unless above genuinely has more to offer — a panel that flips on a
    // few pixels of difference feels broken.
    const below = roomBelow >= Math.min(PANEL_MAX_HEIGHT, roomAbove)
    setAt({
      below,
      top: below ? rect.bottom + GAP : undefined,
      bottom: below ? undefined : window.innerHeight - rect.top + GAP,
      left: rect.left,
      width: rect.width,
      maxHeight: Math.max(MIN_PANEL_HEIGHT,
                          Math.min(PANEL_MAX_HEIGHT, below ? roomBelow : roomAbove)),
    })
  }, [])

  // Before paint, so the panel never renders one frame in the wrong place.
  useLayoutEffect(() => { if (open) place() }, [open, place])

  useEffect(() => {
    if (!open) return
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node
      // The panel is a PORTAL, so it is not a descendant of the trigger —
      // both have to be checked or every click inside it closes the panel.
      if (!triggerRef.current?.contains(target)
          && !panelRef.current?.contains(target)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      // Escape closes THIS, and must not reach the dialog or the page behind.
      if (event.key === 'Escape') { event.stopPropagation(); setOpen(false) }
    }
    // Capture, so a scroll in ANY pane the trigger sits in repositions the
    // panel rather than leaving it floating where the trigger used to be.
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey, true)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [open, place])

  const panel = useCallback((children: ReactNode, minWidth = 240) => {
    if (!open || !at) return null
    const width = Math.min(Math.max(minWidth, at.width), window.innerWidth - EDGE * 2)
    const left = Math.max(EDGE, Math.min(at.left, window.innerWidth - width - EDGE))
    return createPortal(
      <div
        ref={panelRef}
        style={{
          ...popoverStyle,
          top: at.top,
          bottom: at.bottom,
          left,
          width,
          maxHeight: at.maxHeight,
        }}
      >
        {children}
      </div>,
      document.body,
    )
  }, [open, at])

  return { open, setOpen, triggerRef, panel }
}

interface Placement {
  below: boolean
  /** One of these is set; the other stays undefined so CSS anchors that edge. */
  top?: number
  bottom?: number
  left: number
  width: number
  maxHeight: number
}

/** Between the trigger and the panel. */
const GAP = 6
/** Kept clear of every window edge. */
const EDGE = 12
const PANEL_MAX_HEIGHT = 320
/** A floor on the height, so a cramped window gets a small scrolling panel
 *  rather than a two-row sliver nobody can use. The side with more room is
 *  already chosen above; this only matters when neither side has much. */
const MIN_PANEL_HEIGHT = 160

const popoverStyle: CSSProperties = {
  // Fixed, because the portal's parent is the body while the coordinates come
  // from the trigger's viewport rect. The z-index clears every layer the run
  // shell paints (its sticky bar and full-screen grids sit at 9000–9410), so a
  // pill inside any of them still opens on top.
  position: 'fixed', zIndex: 9500,
  overflowY: 'auto',
  background: T.surface, border: `1px solid ${T.border}`,
  borderRadius: RADIUS.md, boxShadow: `0 12px 32px ${T.shadow}`,
  padding: 6, display: 'flex', flexDirection: 'column', gap: 2,
}

const checkRowStyle: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
  borderRadius: RADIUS.sm, cursor: 'pointer', fontSize: TYPE.meta,
  background: 'none', border: 'none', color: T.text, textAlign: 'left',
  width: '100%', fontFamily: 'inherit',
}

/**
 * One pill, several values.
 *
 * Reads as a sentence rather than a count wherever it can: one value shows
 * that value, two show both, and only beyond that does it fall back to
 * "3 kinds" — a bare number is the thing people have to open the menu to
 * understand, which defeats the pill.
 */
export function MultiSelectPill({ label, options, values, onChange, emptyHint }: {
  /** Shown when nothing is picked — "Any status". */
  label: string
  options: FilterOption[]
  values: string[]
  onChange: (next: string[]) => void
  /** Shown in place of the list when there is nothing to pick. */
  emptyHint?: string
}) {
  const { open, setOpen, triggerRef, panel } = usePopover()
  const active = values.length > 0
  const chosen = options.filter((o) => values.includes(o.value))
  const text = !active ? label
    : chosen.length <= 2 && chosen.length === values.length
      ? chosen.map((o) => o.label).join(', ')
      : `${values.length} selected`

  const toggle = (value: string) => {
    onChange(values.includes(value)
      ? values.filter((v) => v !== value)
      : [...values, value])
  }

  return (
    <div ref={triggerRef} style={{ display: 'inline-flex' }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center',
        borderRadius: RADIUS.full,
        border: `1px solid ${active ? T.accent : T.border}`,
        background: active ? T.accentWeak : T.inputBg,
        color: active ? T.accent : T.text,
        maxWidth: 280,
      }}>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'inherit', font: 'inherit', fontSize: TYPE.meta,
            padding: '6px 12px', maxWidth: 250,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</span>
          <Icon name="chevronDown" size={12} />
        </button>
        {active ? (
          <button
            type="button"
            onClick={() => onChange([])}
            aria-label={`Clear ${label}`}
            title={`Clear ${label}`}
            style={{
              display: 'inline-flex', alignItems: 'center', background: 'none',
              border: 'none', cursor: 'pointer', color: 'inherit',
              padding: '0 8px 0 0',
            }}
          >
            <Icon name="close" size={12} />
          </button>
        ) : null}
      </span>

      {panel(
        <div role="listbox" aria-multiselectable
             style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {!options.length ? (
            <p style={{ margin: 0, padding: 8, fontSize: TYPE.small, color: T.textSecondary }}>
              {emptyHint || 'Nothing to choose from yet.'}
            </p>
          ) : options.map((option) => {
            const on = values.includes(option.value)
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={on}
                onClick={() => toggle(option.value)}
                style={{ ...checkRowStyle, background: on ? T.accentWeak : 'none' }}
              >
                <span style={{
                  width: 15, height: 15, flexShrink: 0,
                  borderRadius: 4, display: 'inline-flex',
                  alignItems: 'center', justifyContent: 'center',
                  border: `1px solid ${on ? T.accent : T.borderStrong}`,
                  background: on ? T.accent : T.inputBg,
                  color: '#ffffff',
                }}>
                  {on ? <Icon name="check" size={10} /> : null}
                </span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {option.label}
                </span>
              </button>
            )
          })}
        </div>,
      )}
    </div>
  )
}

// ── the saved list ─────────────────────────────────────────────────────────

/**
 * Saved filters, several of which can be on at once — that is the whole point
 * of storing them: "Overdue reviews" and "Mine" are two questions somebody
 * asks together as often as separately, and each is ANDed with the pills
 * rather than replacing them.
 */
export function SavedFilterMenu({
  filters, activeIds, onToggle, onEdit, onDelete, onNew, canSave,
}: {
  filters: DmsSavedFilter[]
  activeIds: string[]
  onToggle: (id: string) => void
  onEdit: (filter: DmsSavedFilter) => void
  onDelete: (filter: DmsSavedFilter) => void
  onNew: () => void
  canSave: boolean
}) {
  const { open, setOpen, triggerRef, panel } = usePopover()
  const active = activeIds.length > 0
  const activeNames = filters
    .filter((f) => activeIds.includes(f.id))
    .map((f) => f.name)
  const text = !active ? 'Saved filters'
    : activeNames.length === 1 ? activeNames[0]
      : `${activeNames.length} saved filters`

  return (
    <div ref={triggerRef} style={{ display: 'inline-flex' }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center',
        borderRadius: RADIUS.full,
        border: `1px solid ${active ? T.accent : T.border}`,
        background: active ? T.accentWeak : T.inputBg,
        color: active ? T.accent : T.text,
      }}>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'inherit', font: 'inherit', fontSize: TYPE.meta,
            padding: '6px 12px', maxWidth: 260,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</span>
          <Icon name="chevronDown" size={12} />
        </button>
        {active ? (
          <button
            type="button"
            onClick={() => activeIds.forEach(onToggle)}
            aria-label="Turn off the saved filters"
            title="Turn off the saved filters"
            style={{
              display: 'inline-flex', alignItems: 'center', background: 'none',
              border: 'none', cursor: 'pointer', color: 'inherit',
              padding: '0 8px 0 0',
            }}
          >
            <Icon name="close" size={12} />
          </button>
        ) : null}
      </span>

      {panel(
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {!filters.length ? (
            <p style={{ margin: 0, padding: '8px 8px 10px', fontSize: TYPE.small,
                        color: T.textSecondary, lineHeight: 1.5 }}>
              Nothing saved yet. Build a filter and give it a name, and it will
              be one click from here.
            </p>
          ) : filters.map((filter) => {
            const on = activeIds.includes(filter.id)
            return (
              <div key={filter.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <button
                  type="button"
                  onClick={() => onToggle(filter.id)}
                  style={{ ...checkRowStyle, flex: 1, minWidth: 0,
                           background: on ? T.accentWeak : 'none' }}
                >
                  <span style={{
                    width: 15, height: 15, flexShrink: 0,
                    borderRadius: 4, display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center',
                    border: `1px solid ${on ? T.accent : T.borderStrong}`,
                    background: on ? T.accent : T.inputBg, color: '#ffffff',
                  }}>
                    {on ? <Icon name="check" size={10} /> : null}
                  </span>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden',
                                 textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {filter.name}
                  </span>
                  {filter.shared ? (
                    <span style={{ fontSize: TYPE.small, color: T.textSecondary,
                                   flexShrink: 0 }}
                          title="Shared with everyone in this repository">
                      shared
                    </span>
                  ) : null}
                </button>
                {/* Only what this person may actually do — an edit button that
                    refuses on click is worse than no button. */}
                {filter.can_edit ? (
                  <>
                    <button type="button" onClick={() => onEdit(filter)}
                            title={`Change "${filter.name}"`}
                            aria-label={`Change "${filter.name}"`}
                            style={iconBtnStyle}>
                      <Icon name="pencil" size={13} />
                    </button>
                    <button type="button" onClick={() => onDelete(filter)}
                            title={`Delete "${filter.name}"`}
                            aria-label={`Delete "${filter.name}"`}
                            style={iconBtnStyle}>
                      <Icon name="trash" size={13} />
                    </button>
                  </>
                ) : null}
              </div>
            )
          })}
          <button type="button" onClick={() => { setOpen(false); onNew() }}
                  style={{ ...checkRowStyle, color: T.accent, marginTop: 4 }}>
            <Icon name="plus" size={12} />
            {canSave ? 'Build a new filter' : 'Build a filter for this visit'}
          </button>
        </div>,
        300,
      )}
    </div>
  )
}

const iconBtnStyle: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 26, height: 26, flexShrink: 0, borderRadius: RADIUS.sm,
  background: 'none', border: 'none', cursor: 'pointer', color: T.textSecondary,
}

// ── the builder ────────────────────────────────────────────────────────────

const labelStyle: CSSProperties = {
  fontSize: TYPE.small, color: T.textSecondary, fontWeight: 500,
}

const selectStyle: CSSProperties = { ...inputStyle, maxWidth: 240 }

/** Root-level rows, or the rows of one nested group. */
type Row = DmsCondition | DmsConditionGroup

function isGroup(row: Row): row is DmsConditionGroup {
  return Array.isArray((row as DmsConditionGroup).conditions)
}

function emptyCondition(fields: FilterField[]): DmsCondition {
  const first = fields[0]
  return {
    field: first?.value || 'doc:title',
    op: (operatorsFor(first)[0]?.value || 'contains') as DmsCondition['op'],
    value: '',
  }
}

/**
 * Build or change one filter.
 *
 * Two ways out on purpose: **Apply** uses it for this visit without keeping it
 * (most filters are asked once), and **Save** names it for the saved list. A
 * builder that could only save would fill the list with one-offs.
 */
export function FilterBuilderModal({
  fields, initial, initialName, initialShared, editingId,
  canShare, canSave, onApply, onSave, onClose, busy, error,
}: {
  fields: FilterField[]
  initial?: DmsConditionGroup | null
  initialName?: string
  initialShared?: boolean
  /** Set when changing a saved filter rather than building a new one. */
  editingId?: string
  canShare: boolean
  canSave: boolean
  onApply: (group: DmsConditionGroup | null) => void
  onSave: (input: { name: string; shared: boolean; group: DmsConditionGroup | null }) => void
  onClose: () => void
  busy?: boolean
  error?: string
}) {
  const [group, setGroup] = useState<DmsConditionGroup>(
    () => initial && initial.conditions?.length
      ? initial
      : { join: 'and', conditions: [emptyCondition(fields)] },
  )
  const [name, setName] = useState(initialName || '')
  const [shared, setShared] = useState(!!initialShared)

  const fieldsByValue = useMemo(
    () => new Map(fields.map((f) => [f.value, f])), [fields],
  )
  const grouped = useMemo(() => {
    const out = new Map<string, FilterField[]>()
    for (const field of fields) {
      const list = out.get(field.group) || []
      list.push(field)
      out.set(field.group, list)
    }
    return [...out.entries()]
  }, [fields])

  /** Replace one row, addressed by its index (and, in a group, its own). */
  const setRow = useCallback((index: number, next: Row | null, inner?: number) => {
    setGroup((prev) => {
      const rows = [...prev.conditions]
      if (inner === undefined) {
        if (next === null) rows.splice(index, 1)
        else rows[index] = next
        return { ...prev, conditions: rows }
      }
      const nested = rows[index] as DmsConditionGroup
      if (!isGroup(nested)) return prev
      const innerRows = [...nested.conditions]
      if (next === null) innerRows.splice(inner, 1)
      else innerRows[inner] = next
      // A group emptied of its last condition is noise on the screen and
      // nothing in the query — it goes with the condition.
      if (!innerRows.length) {
        rows.splice(index, 1)
        return { ...prev, conditions: rows }
      }
      rows[index] = { ...nested, conditions: innerRows }
      return { ...prev, conditions: rows }
    })
  }, [])

  const pruned = useMemo(() => pruneGroup(group), [group])
  const count = countConditions(pruned)

  const renderRow = (row: Row, index: number, inner?: number) => (
    <ConditionRow
      key={inner === undefined ? `r${index}` : `r${index}-${inner}`}
      condition={row as DmsCondition}
      fields={fields}
      grouped={grouped}
      fieldsByValue={fieldsByValue}
      onChange={(next) => setRow(index, next, inner)}
      onRemove={() => setRow(index, null, inner)}
    />
  )

  return createPortal(
    <div style={overlayStyle} onClick={onClose}>
      <div style={panelStyle} role="dialog" aria-modal="true"
           aria-label={editingId ? 'Change this filter' : 'Build a filter'}
           onClick={(event) => event.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <strong style={{ fontSize: TYPE.heading }}>
            {editingId ? 'Change this filter' : 'Build a filter'}
          </strong>
          <span style={{ fontSize: TYPE.small, color: T.textSecondary }}>
            Narrows whatever the pills above have already selected.
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={labelStyle}>Documents matching</span>
          <JoinToggle
            value={group.join}
            onChange={(join) => setGroup((prev) => ({ ...prev, join }))}
          />
          <span style={labelStyle}>of these</span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8,
                      maxHeight: '46vh', overflowY: 'auto', paddingRight: 2 }}>
          {group.conditions.map((row, index) => (
            isGroup(row) ? (
              <div key={`g${index}`} style={{
                border: `1px solid ${T.border}`, borderRadius: RADIUS.md,
                padding: 10, display: 'flex', flexDirection: 'column', gap: 8,
                background: T.surface2,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={labelStyle}>Any document matching</span>
                  <JoinToggle
                    value={row.join}
                    onChange={(join) => setRow(index, { ...row, join })}
                  />
                  <div style={{ flex: 1 }} />
                  <button type="button" style={iconBtnStyle}
                          aria-label="Remove this group"
                          title="Remove this group"
                          onClick={() => setRow(index, null)}>
                    <Icon name="trash" size={13} />
                  </button>
                </div>
                {row.conditions.map((child, childIndex) =>
                  renderRow(child, index, childIndex))}
                <button
                  type="button"
                  style={{ ...btnStyle, alignSelf: 'flex-start', border: 'none',
                           color: T.accent, background: 'none' }}
                  onClick={() => setRow(index, {
                    ...row,
                    conditions: [...row.conditions, emptyCondition(fields)],
                  })}
                >
                  <Icon name="plus" size={12} /> Condition
                </button>
              </div>
            ) : renderRow(row, index)
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            style={{ ...btnStyle, border: 'none', color: T.accent, background: 'none' }}
            onClick={() => setGroup((prev) => ({
              ...prev, conditions: [...prev.conditions, emptyCondition(fields)],
            }))}
          >
            <Icon name="plus" size={12} /> Condition
          </button>
          <button
            type="button"
            style={{ ...btnStyle, border: 'none', color: T.accent, background: 'none' }}
            onClick={() => setGroup((prev) => ({
              ...prev,
              conditions: [...prev.conditions, {
                join: prev.join === 'and' ? 'or' : 'and',
                conditions: [emptyCondition(fields)],
              }],
            }))}
            title="A bracketed set — matched as a whole against the rest"
          >
            <Icon name="plus" size={12} /> Group
          </button>
        </div>

        {canSave ? (
          <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10,
                        display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={labelStyle}>Name it to keep it</span>
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Overdue reviews"
                style={{ ...inputStyle, width: '100%' }}
              />
            </label>
            {canShare ? (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8,
                              fontSize: TYPE.meta }}>
                <input type="checkbox" checked={shared}
                       onChange={(event) => setShared(event.target.checked)} />
                Share it with everyone in this repository
              </label>
            ) : null}
          </div>
        ) : (
          <p style={{ margin: 0, fontSize: TYPE.small, color: T.textSecondary }}>
            This repository cannot keep saved filters, so this one applies to
            this visit only.
          </p>
        )}

        {error ? (
          <p style={{ margin: 0, fontSize: TYPE.small, color: T.error }}>{error}</p>
        ) : null}

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: TYPE.small, color: T.textSecondary }}>
            {count ? `${count} condition${count === 1 ? '' : 's'}`
                   : 'Nothing to match on yet'}
          </span>
          <div style={{ flex: 1 }} />
          <button type="button" style={btnStyle} onClick={onClose}>Cancel</button>
          <button type="button" style={btnStyle} disabled={busy}
                  onClick={() => onApply(pruned)}>
            Apply without saving
          </button>
          <button
            type="button"
            style={{ ...btnPrimaryStyle, opacity: (!canSave || !name.trim() || busy) ? 0.5 : 1 }}
            disabled={!canSave || !name.trim() || !!busy}
            onClick={() => onSave({ name: name.trim(), shared, group: pruned })}
          >
            {busy ? 'Saving…' : editingId ? 'Save changes' : 'Save filter'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

function JoinToggle({ value, onChange }: {
  value: 'and' | 'or'
  onChange: (join: 'and' | 'or') => void
}) {
  return (
    <span style={{ display: 'inline-flex', border: `1px solid ${T.border}`,
                   borderRadius: RADIUS.md, overflow: 'hidden' }}>
      {(['and', 'or'] as const).map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onChange(option)}
          style={{
            padding: '4px 12px', border: 'none', cursor: 'pointer',
            fontSize: TYPE.small, fontFamily: 'inherit', fontWeight: 600,
            background: value === option ? T.accentWeak : T.inputBg,
            color: value === option ? T.accent : T.textSecondary,
          }}
        >
          {option === 'and' ? 'all' : 'any'}
        </button>
      ))}
    </span>
  )
}

const overlayStyle: CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
}

const panelStyle: CSSProperties = {
  background: T.surface, color: T.text,
  border: `1px solid ${T.border}`, borderRadius: RADIUS.lg,
  padding: 16, width: 'min(760px, 96vw)',
  display: 'flex', flexDirection: 'column', gap: 12,
  boxShadow: `0 16px 40px ${T.shadow}`,
}

/** One comparison: field, operator, and whatever operands it takes. */
function ConditionRow({
  condition, fields, grouped, fieldsByValue, onChange, onRemove,
}: {
  condition: DmsCondition
  fields: FilterField[]
  grouped: Array<[string, FilterField[]]>
  fieldsByValue: Map<string, FilterField>
  onChange: (next: DmsCondition) => void
  onRemove: () => void
}) {
  const field = fieldsByValue.get(condition.field)
  const operators = operatorsFor(field)
  const operator = operators.find((o) => o.value === condition.op) || operators[0]
  const operands = operator?.operands ?? 1

  /** Changing the field can strand an operator the new one does not offer
   *  (a date has no "contains"), so the operator and value reset with it. */
  const pickField = (value: string) => {
    const next = fieldsByValue.get(value)
    const nextOps = operatorsFor(next)
    onChange({
      field: value,
      op: (nextOps[0]?.value || 'contains') as DmsCondition['op'],
      value: nextOps[0]?.operands === 'many' ? [] : '',
    })
  }

  const pickOperator = (value: string) => {
    const next = operators.find((o) => o.value === value)
    onChange({
      ...condition,
      op: value as DmsCondition['op'],
      // Carry the operand across where the shape matches, drop it where it
      // does not — a stale "%draft%" behind "is between" is a trap.
      value: next?.operands === 'many'
        ? (Array.isArray(condition.value) ? condition.value : [])
        : next?.operands === 1 ? (Array.isArray(condition.value) ? '' : condition.value)
          : undefined,
      from: next?.operands === 2 ? condition.from : undefined,
      to: next?.operands === 2 ? condition.to : undefined,
    })
  }

  const inputType = field?.kind === 'date' ? 'date'
    : field?.kind === 'number' ? 'number' : 'text'

  return (
    <div style={{
      display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
    }}>
      <select value={condition.field} style={selectStyle}
              aria-label="Field"
              onChange={(event) => pickField(event.target.value)}>
        {grouped.map(([group, list]) => (
          <optgroup key={group} label={group}>
            {list.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </optgroup>
        ))}
      </select>

      <select value={condition.op} style={{ ...selectStyle, maxWidth: 170 }}
              aria-label="Operator"
              onChange={(event) => pickOperator(event.target.value)}>
        {operators.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>

      {operands === 'many' ? (
        field?.options?.length ? (
          <MultiSelectPill
            label="Choose values"
            options={field.options}
            values={(Array.isArray(condition.value) ? condition.value : []).map(String)}
            onChange={(next) => onChange({ ...condition, value: next })}
          />
        ) : (
          <input
            style={{ ...inputStyle, minWidth: 220 }}
            placeholder="Values, separated by commas"
            aria-label="Values"
            value={(Array.isArray(condition.value) ? condition.value : []).join(', ')}
            onChange={(event) => onChange({
              ...condition,
              value: event.target.value.split(',').map((v) => v.trim()).filter(Boolean),
            })}
          />
        )
      ) : null}

      {operands === 1 ? (
        field?.options?.length ? (
          <select
            style={selectStyle}
            aria-label="Value"
            value={String(condition.value ?? '')}
            onChange={(event) => onChange({ ...condition, value: event.target.value })}
          >
            <option value="">—</option>
            {field.options.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        ) : field?.kind === 'boolean' ? (
          <select
            style={selectStyle}
            aria-label="Value"
            value={String(condition.value ?? '')}
            onChange={(event) => onChange({ ...condition, value: event.target.value })}
          >
            <option value="">—</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        ) : (
          <input
            type={inputType}
            style={{ ...inputStyle, minWidth: 180 }}
            aria-label="Value"
            value={String(condition.value ?? '')}
            onChange={(event) => onChange({ ...condition, value: event.target.value })}
          />
        )
      ) : null}

      {operands === 2 ? (
        <>
          <input
            type={inputType}
            style={{ ...inputStyle, minWidth: 150 }}
            aria-label="From"
            value={String(condition.from ?? '')}
            onChange={(event) => onChange({ ...condition, from: event.target.value })}
          />
          <span style={{ fontSize: TYPE.small, color: T.textSecondary }}>and</span>
          <input
            type={inputType}
            style={{ ...inputStyle, minWidth: 150 }}
            aria-label="To"
            value={String(condition.to ?? '')}
            onChange={(event) => onChange({ ...condition, to: event.target.value })}
          />
        </>
      ) : null}

      <div style={{ flex: 1 }} />
      <button type="button" style={iconBtnStyle} onClick={onRemove}
              aria-label="Remove this condition" title="Remove this condition">
        <Icon name="close" size={13} />
      </button>
    </div>
  )
}
